import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { API } from "../../config";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import { Autoplay, Navigation, Pagination } from "swiper/modules";

interface Testimonial {
  testimonial_id: number;
  title: string | null;
  description: string | null;
  author: string | null;
  role: string | null;
  display_order: number;
  record_status_id: number;
}

const LIMIT = 120;

function stripHtml(html: string): string {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent ?? tmp.innerText ?? "").replace(/\u00a0/g, " ").trim();
}

export default function TestimonialsSlider() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetch(`${API}/testimonials`)
      .then((res) => res.json())
      .then((data) => {
        const active = (data.testimonials as Testimonial[]).filter(
          (t) => t.record_status_id === 1
        );
        active.sort((a, b) => a.display_order - b.display_order);
        setTestimonials(active);
      })
      .catch((err) => console.error("Failed to load testimonials:", err))
      .finally(() => setLoaded(true));
  }, []);

  return (
    <section id="testimonials" className="pb-60 py-2 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="heading-bold headingcolor">Hear from Our Community</h2>
          <p className="subheading pt-4">
            Voices from parents, educators, and institutions we collaborate with.
          </p>
        </div>
        {loaded && testimonials.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-300 mb-4" aria-hidden="true">
              <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"></path>
              <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"></path>
            </svg>
            <h3 className="text-lg font-semibold text-gray-500 mb-1">No testimonials available at the moment</h3>
            <p className="text-sm text-gray-600">Check back soon to hear what our community has to say.</p>
          </div>
        ) : (
        <div className="wrapper px-55">
          <Swiper
            modules={[Autoplay, Pagination, Navigation]}
            loop
            watchOverflow={false}
            style={{ alignItems: "stretch" }}
            navigation={{
              nextEl: ".test-next",
              prevEl: ".test-prev",
            }}
            pagination={{
              el: ".test-pagination",
              clickable: true,
            }}
            className="testimonialsSwiper"
            spaceBetween={30}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            slidesPerView={3}
            breakpoints={{
              320: { slidesPerView: 1, spaceBetween: 15 },
              640: { slidesPerView: 1, spaceBetween: 20 },
              768: { slidesPerView: 2, spaceBetween: 25 },
              1024: { slidesPerView: 3, spaceBetween: 30 },
              1280: { slidesPerView: 3, spaceBetween: 30 },
            }}
          >
            {testimonials.map((t) => (
              <SwiperSlide key={t.testimonial_id} className="h-auto">
                <div className="h-full">
                  {(() => {
                    const plain = stripHtml(t.description ?? "");
                    const isLong = plain.length > LIMIT;
                    const isExpanded = expanded.has(t.testimonial_id);
                    return (
                      <div className={`bg-[#fff6d5] rounded-lg p-6 shadow flex flex-col ${isExpanded ? "min-h-[260px]" : "h-[260px]"}`}>
                        {/* Top Quote */}
                        <div className="leading-none flex-shrink-0">
                          <img src="/images/cama_top.png" alt="" aria-hidden="true" width={35} height={26} loading="lazy" decoding="async" />
                        </div>

                        {/* Testimonial Text */}
                        <div className="text-gray-700 text-sm leading-relaxed mt-2 flex-1 break-words space-y-2">
                          {isExpanded ? (
                            plain.split("\n").filter(Boolean).map((line, i) => <p key={i}>{line}</p>)
                          ) : (
                            <span>{plain.slice(0, LIMIT).trimEnd()}{isLong && "..."}</span>
                          )}
                          {isLong && !isExpanded && (
                            <button
                              onClick={() => setExpanded((prev) => new Set(prev).add(t.testimonial_id))}
                              className="text-red-700 font-semibold hover:underline ml-1"
                            >
                              more
                            </button>
                          )}
                          {isLong && isExpanded && (
                            <button
                              onClick={() => setExpanded((prev) => { const s = new Set(prev); s.delete(t.testimonial_id); return s; })}
                              className="text-red-700 font-semibold hover:underline ml-1"
                            >
                              less
                            </button>
                          )}
                        </div>

                        {/* Bottom Quote */}
                        <div className="flex justify-end flex-shrink-0">
                          <img src="/images/cama_bottom.png" alt="" aria-hidden="true" width={35} height={26} loading="lazy" decoding="async" />
                        </div>

                        {/* Profile Section */}
                        <div className="flex items-center gap-3 mt-3 flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                            {t.author ? t.author.charAt(0).toUpperCase() : "?"}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800 text-sm">{t.author}</p>
                            <p className="text-xs text-gray-600">{t.role}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="test-prev">
            <b>❮</b>
          </div>
          <div className="test-next">
            <b>❯</b>
          </div>
          <div className="test-pagination mt-4 text-center"></div>
        </div>
        )}

        <div className="text-center mt-16">
          <div className="md:flex bg-yellow-700 text-white rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="mb-4">
              <img src="/images/phonicsLogo.webp" className="ready-join-img object-contain" alt="Phonics Power" width={1596} height={612} loading="lazy" decoding="async" />
            </div>
            <div className="left-4 px-55 md:text-left">
              <h3 className="ready-join-head">
                Ready to Join Our Success Stories?
              </h3>
              <p className="ready-join-p pt-4">
                Take the first step towards confident communication and stronger English skills.
              </p>
              <div className="md:flex flex-col sm:flex-row gap-4">
                <Link to="/contact-us">
                  <button className="bg-white text-red-600 ready-btn my-3 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap">
                    Book an Assessment
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
